import { ArrowLeft, History } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PartnerAccess } from '@/components/Accounts/PartnerAccess';
import { Field, FormSection } from '@/components/Admin/AdminFormFields';
import { AutosaveTextInput } from '@/components/Admin/Autosave/AutosaveTextInput';
import { PartnerActivityCard } from '@/components/Admin/PartnerActivityCard';
import { SlugField } from '@/components/Admin/SlugField';
import { Button } from '@/components/ui/button';
import { createPartner, getPartner, isPartnerCodeTaken, isPartnerIdTaken } from '@/data/partnersRepo';
import { DocumentIdTakenError } from '@/data/util_documentIds';
import { HistoryPanel } from '@/hooks/autosave/HistoryPanel';
import { TopWhiteBarEditControls } from '@/hooks/autosave/TopWhiteBarEditControls';
import { UndoRedoButtons } from '@/hooks/autosave/UndoRedoButtons';
import { useAutosavePartner } from '@/hooks/autosave/useAutosavePartner';
import { undoRedoStorageKey, useUndoRedoStack } from '@/hooks/autosave/useUndoRedoStack';
import { describeChange, type ValueFormatterType } from '@/hooks/autosave/util_historyMessage';
import { useSaveStack } from '@/hooks/autosave/util_saveStack';
import { normaliseShortCode, shortCodeLength, validateShortCode } from '@/types/types_code';
import { coll_partnersHistory, makeEmptyPartner, type PartnerType } from '@/types/types_partner';
import { slugify, validateSlugForCreate } from '@/util/util_slug';

interface PagePartnerEditProps {
  mode: 'new' | 'edit';
}

/**
 * Create / edit form for a single partner.
 *
 * In `edit` mode the form autosaves each field to Firestore on commit (blur),
 * with a sticky top bar, local undo/redo, and persisted history — same as the
 * experience edit page. In `new` mode it keeps a Create button, since there's
 * nothing to autosave until the document exists.
 */
export function PagePartnerEdit({ mode }: PagePartnerEditProps) {
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<PartnerType>(() => makeEmptyPartner());
  const formRef = useRef(form);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Why the Register button refused, shown above it. Only reachable in `new`
  // mode: an edit reports its problems on the field itself, since it saves per
  // field.
  const [createError, setCreateError] = useState<string | null>(null);
  // The web address field's own complaint. `new` mode only — an existing
  // partner's id is fixed, so the field is not rendered at all.
  const [slugError, setSlugError] = useState<string | null>(null);

  const saveStack = useSaveStack();
  const autosave = useAutosavePartner(mode === 'edit' ? partnerId : undefined, saveStack);
  const undoRedo = useUndoRedoStack();
  const { loadFor: loadUndoRedoFor } = undoRedo;

  useEffect(() => {
    if (mode !== 'edit' || !partnerId) return;
    let cancelled = false;
    getPartner(partnerId)
      .then((partner) => {
        if (cancelled) return;
        if (partner) {
          formRef.current = partner;
          setForm(partner);
          // Restores this document's persisted undo stack (and discards it if the
          // record has moved on since it was written).
          loadUndoRedoFor(
            undoRedoStorageKey('partner', partnerId),
            (field) => formRef.current[field as keyof PartnerType],
          );
        } else {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, partnerId, loadUndoRedoFor]);

  // Write a field value: reflect locally and, in edit mode, autosave it along
  // with a persisted history step. Shared by direct edits and by undo/redo.
  function applyChange(field: string, value: unknown, userAction: string, isUndoStep = false) {
    if (mode === 'edit') {
      const { newHistory } = autosave(userAction, { [field]: value }, formRef.current.history, isUndoStep);
      formRef.current = { ...formRef.current, [field]: value, history: newHistory };
      setForm(formRef.current);
    } else {
      formRef.current = { ...formRef.current, [field]: value };
      setForm(formRef.current);
    }
  }

  // A user edit: apply it and (edit mode) record it on the undo stack.
  function commit<K extends keyof PartnerType>(
    key: K,
    value: PartnerType[K],
    label: string,
    format?: ValueFormatterType,
  ) {
    const prevValue = formRef.current[key];
    if (Object.is(prevValue, value)) return;
    const describe = (v: unknown) => describeChange(label, v, format);
    applyChange(key, value, describe(value));
    if (mode === 'edit') {
      undoRedo.record({
        field: key,
        prevValue,
        nextValue: value,
        undoMessage: describe(prevValue),
        redoMessage: describe(value),
      });
    }
  }

  // Why this code cannot be used, or null. Shape first — a malformed code is
  // worth saying without a round trip — then whether another partner holds it,
  // which only Firestore can answer. `partnerId` is excluded so a record never
  // clashes with the code it already has; in `new` mode nothing exists to
  // exclude yet.
  async function validateCode(value: string): Promise<string | null> {
    const shapeError = validateShortCode(value);
    if (shapeError) return shapeError;
    const code = normaliseShortCode(value);
    if (!code) return null;
    try {
      return (await isPartnerCodeTaken(code, partnerId))
        ? `Code ${code} is already used by another partner.`
        : null;
    } catch (e: unknown) {
      // Better to say the check didn't happen than to save a code over one that
      // might already exist.
      console.error('Partner code check failed', e);
      return 'Could not check whether this code is free. Try again.';
    }
  }

  // The web address, written straight onto the form. Stable so that SlugField's
  // effect — which fills the field in from the name — does not re-run per render.
  const setSlug = useCallback((slug: string) => {
    formRef.current = { ...formRef.current, id: slug };
    setForm(formRef.current);
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setCreateError(null);
    // Both checked again here, not only on blur: the fields validate what was in
    // them when the user left them, and the partner list may have moved on since.
    const codeError = await validateCode(formRef.current.code);
    if (codeError) {
      setCreateError(codeError);
      setSaving(false);
      return;
    }
    const id = slugify(formRef.current.id || formRef.current.name);
    const idError = await validateSlugForCreate(id, isPartnerIdTaken, 'partner');
    if (idError) {
      setSlugError(idError);
      setCreateError('Check the highlighted field above.');
      setSaving(false);
      return;
    }
    const toSave: PartnerType = {
      ...formRef.current,
      code: normaliseShortCode(formRef.current.code),
      id,
    };
    try {
      await createPartner(toSave);
    } catch (e: unknown) {
      // The id was free a moment ago and is not any more — somebody else created
      // it in between, and the transaction refused rather than writing over them.
      if (e instanceof DocumentIdTakenError) {
        setSlugError('That web address was taken while this form was open. Choose a different one.');
        setCreateError('Check the highlighted field above.');
      } else {
        console.error('Create partner failed', e);
        setCreateError('The partner could not be created. Try again.');
      }
      setSaving(false);
      return;
    }
    setSaving(false);
    navigate('/admin/partners');
  }

  if (loading) {
    return <div className='tw:py-16 tw:text-center tw:text-muted-foreground'>Loading…</div>;
  }

  if (notFound) {
    return (
      <div className='tw:py-16 tw:text-center'>
        <p className='tw:text-lg'>Partner not found.</p>
        <Link to='/admin/partners' className='tw:mt-4 tw:inline-block tw:text-primary tw:underline'>
          Back to partners
        </Link>
      </div>
    );
  }

  const isEdit = mode === 'edit';

  const fields = (
    <div className='tw:space-y-8'>
      {/* Partner */}
      <FormSection title='Partner'>
        <Field label='Code'>
          <AutosaveTextInput
            currentValue={form.code}
            placeholder='AM'
            maxLength={shortCodeLength}
            normalise={normaliseShortCode}
            validate={validateCode}
            className='tw:w-24 tw:uppercase'
            onCommitChange={(v) => commit('code', v, 'code')}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            The two letters that stand for this partner inside a tour request code. Eighty Days sets it
            &mdash; partners cannot change their own. No two partners may share one.
          </p>
        </Field>
        <Field label='Partner Name' required>
          <AutosaveTextInput currentValue={form.name} onCommitChange={(v) => commit('name', v, 'partner name')} />
        </Field>
        {!isEdit && (
          <SlugField
            label='Record ID'
            name={form.name}
            value={form.id}
            onChange={setSlug}
            isTaken={isPartnerIdTaken}
            entityLabel='partner'
            error={slugError}
            onErrorChange={setSlugError}
          />
        )}
        <Field label='Brand / Group'>
          <AutosaveTextInput
            currentValue={form.brand}
            placeholder='e.g., Aman Resorts'
            onCommitChange={(v) => commit('brand', v, 'brand')}
          />
        </Field>
        <Field label='Website'>
          <AutosaveTextInput
            currentValue={form.website}
            placeholder='example.com'
            onCommitChange={(v) => commit('website', v, 'website')}
          />
        </Field>
        <Field label='Partner Address' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            multiline
            rows={2}
            currentValue={form.address}
            placeholder='Full address'
            onCommitChange={(v) => commit('address', v, 'partner address')}
          />
        </Field>
      </FormSection>

      {/* Primary contact */}
      <FormSection title='Primary Contact'>
        <Field label='Contact Name'>
          <AutosaveTextInput
            currentValue={form.contactName}
            onCommitChange={(v) => commit('contactName', v, 'contact name')}
          />
        </Field>
        <Field label='Position / Title'>
          <AutosaveTextInput
            currentValue={form.position}
            placeholder='e.g., Concierge Manager'
            onCommitChange={(v) => commit('position', v, 'position')}
          />
        </Field>
        <Field label='Contact Email'>
          <AutosaveTextInput
            currentValue={form.contactEmail}
            placeholder='concierge@example.com'
            onCommitChange={(v) => commit('contactEmail', v, 'contact email')}
          />
        </Field>
        <Field label='Phone Number'>
          <AutosaveTextInput
            currentValue={form.phone}
            placeholder='+81-3-XXXX-XXXX'
            onCommitChange={(v) => commit('phone', v, 'phone')}
          />
        </Field>
      </FormSection>

      {/* Accounting */}
      <FormSection title='Accounting'>
        <Field label='Accounting Contact Name'>
          <AutosaveTextInput
            currentValue={form.accountingName}
            placeholder='Name for invoicing'
            onCommitChange={(v) => commit('accountingName', v, 'accounting name')}
          />
        </Field>
        <Field label='Accounting Email'>
          <AutosaveTextInput
            currentValue={form.accountingEmail}
            placeholder='accounting@example.com'
            onCommitChange={(v) => commit('accountingEmail', v, 'accounting email')}
          />
        </Field>
        <Field label='Billing Address' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            multiline
            rows={2}
            currentValue={form.billingAddress}
            placeholder='Full billing address'
            onCommitChange={(v) => commit('billingAddress', v, 'billing address')}
          />
        </Field>
      </FormSection>
    </div>
  );

  // ----- Edit mode: sticky autosave bar, no submit button -----
  if (isEdit) {
    return (
      <div className='tw:mx-auto tw:max-w-6xl'>
        <TopWhiteBarEditControls
          saveStack={saveStack}
          leftSlot={
            <Link
              to='/admin/partners'
              className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
            >
              <ArrowLeft className='tw:size-4' /> Partners
            </Link>
          }
          middleSlot={
            <UndoRedoButtons
              canUndo={undoRedo.canUndo}
              canRedo={undoRedo.canRedo}
              undoLabel={undoRedo.undoLabel}
              redoLabel={undoRedo.redoLabel}
              onUndo={() => undoRedo.undo((f, v, a) => applyChange(f, v, a, true))}
              onRedo={() => undoRedo.redo((f, v, a) => applyChange(f, v, a, true))}
            />
          }
        >
          <button
            type='button'
            onClick={() => setShowHistory(true)}
            className='tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-ed-stone tw:transition-colors tw:hover:text-ed-gold'
          >
            <History className='tw:size-4' /> History
            {!!form.history?.lastStep && <span className='tw:text-ed-stone/70'>({form.history.lastStep})</span>}
          </button>
        </TopWhiteBarEditControls>
        <header className='tw:mb-8'>
          <h1 className='tw:font-serif tw:text-3xl tw:font-light'>Edit — {form.name}</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>Changes save automatically.</p>
        </header>
        {fields}
        {/* Accounts, invitations and activity only make sense once the partner
            document exists, so they are absent from `new` mode. */}
        <div className='tw:mt-8'>
          <PartnerAccess mode='admin' partnerId={partnerId!} partnerName={form.name} />
        </div>
        <div className='tw:mt-8'>
          <PartnerActivityCard partnerId={partnerId!} />
        </div>
        <HistoryPanel
          key={partnerId}
          show={showHistory}
          onClose={() => setShowHistory(false)}
          history={form.history}
          historyCollectionName={coll_partnersHistory}
        />
      </div>
    );
  }

  // ----- New mode: create-then-navigate, no autosave -----
  return (
    <form onSubmit={handleCreate} className='tw:mx-auto tw:max-w-6xl'>
      <header className='tw:mb-8'>
        <Link
          to='/admin/partners'
          className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
        >
          <ArrowLeft className='tw:size-4' /> Partners
        </Link>
        <h1 className='tw:mt-2 tw:font-serif tw:text-3xl tw:font-light'>Register New Partner</h1>
      </header>

      {fields}

      {createError && (
        <p className='tw:mt-6 tw:text-right tw:text-sm tw:text-destructive'>{createError}</p>
      )}

      <div className='tw:mt-8 tw:flex tw:justify-end tw:gap-3'>
        <Button asChild variant='outline' type='button'>
          <Link to='/admin/partners'>Cancel</Link>
        </Button>
        <Button type='submit' disabled={!form.name.trim()} loading={saving}>
          {saving ? 'Saving…' : 'Register Partner'}
        </Button>
      </div>
    </form>
  );
}
